import { gcm } from '@noble/ciphers/aes';
import { randomBytes } from '@noble/ciphers/webcrypto';
import { keccak_256 } from '@noble/hashes/sha3';
import { bytesToHex } from '@noble/hashes/utils';

/**
 * Cryptographic utilities for secure voting
 */

/**
 * Generate a random AES-256 key
 */
export function generateAESKey(): Uint8Array {
  return randomBytes(32); // 256 bits
}

/**
 * Generate a random IV for AES-GCM
 */
export function generateIV(): Uint8Array {
  return randomBytes(12); // 96 bits recommended for GCM
}

/**
 * Encrypt data using AES-256-GCM
 * @param data Data to encrypt (will be JSON stringified)
 * @param key AES-256 key (32 bytes)
 * @returns Object with encrypted data (with IV prepended) and separate IV
 */
export function encryptVote(data: any, key: Uint8Array): { encryptedData: string; iv: Uint8Array } {
  // Stringify the data
  const plaintext = JSON.stringify(data);
  const plaintextBytes = new TextEncoder().encode(plaintext);

  // Generate random IV
  const iv = generateIV();

  // Encrypt using AES-256-GCM
  const cipher = gcm(key, iv);
  const ciphertext = cipher.encrypt(plaintextBytes);

  // Prepend IV to ciphertext (IV is needed for decryption)
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv);
  combined.set(ciphertext, iv.length);

  // Return encrypted data with IV prepended (for compatibility) and separate IV
  return {
    encryptedData: btoa(String.fromCharCode(...combined)),
    iv,
  };
}

/**
 * Decrypt data using AES-256-GCM
 * @param encryptedData Base64 encoded encrypted data with IV prepended
 * @param key AES-256 key (32 bytes)
 * @returns Decrypted data (parsed from JSON)
 */
export function decryptVote(encryptedData: string, key: Uint8Array): any {
  // Decode from base64
  const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

  // Extract IV (first 12 bytes) and ciphertext
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  // Decrypt using AES-256-GCM
  const cipher = gcm(key, iv);
  const plaintext = cipher.decrypt(ciphertext);

  // Parse JSON
  const plaintextStr = new TextDecoder().decode(plaintext);
  return JSON.parse(plaintextStr);
}

/**
 * Hash data using Keccak-256 (Ethereum-style)
 * @param data Data to hash (string or bytes)
 * @returns Hex string with 0x prefix
 */
export function hashKeccak256(data: string | Uint8Array): string {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  const hash = keccak_256(bytes);
  return '0x' + bytesToHex(hash);
}

/**
 * Simple blind signature implementation (simplified for demo)
 * In production, use proper RSA blind signature libraries
 */
export interface BlindedMessage {
  blindedHash: string;
  blindingFactor: string;
}

/**
 * Blind a message hash (simplified implementation)
 * @param messageHash Keccak256 hash of the message
 * @param publicKey RSA public key in PEM format
 * @returns Blinded message and blinding factor
 */
export function blindMessage(messageHash: string, _publicKey: string): BlindedMessage {
  // In a real implementation, this would:
  // 1. Import the RSA public key
  // 2. Generate a random blinding factor
  // 3. Blind the message: m' = m * r^e mod n

  // For simplicity, we'll encode the message hash as base64
  // This is NOT cryptographically secure blind signing!
  const blindingFactor = bytesToHex(randomBytes(32));
  const blindedHash = btoa(messageHash + blindingFactor);

  return {
    blindedHash,
    blindingFactor,
  };
}

/**
 * Unblind a signature (simplified implementation)
 * @param blindedSignature Signature from server
 * @param blindingFactor Blinding factor from blindMessage
 * @returns Unblinded signature
 */
export function unblindSignature(
  blindedSignature: string,
  _blindingFactor: string
): string {
  // In a real implementation, this would:
  // s = s' * r^(-1) mod n

  // For simplicity, we'll just return the signature
  // This is NOT cryptographically secure!
  return blindedSignature;
}

/**
 * Generate a receipt code format
 */
export function formatReceiptCode(code: string): string {
  // Format: XXXX-XXXX-XXXX
  return code.match(/.{1,4}/g)?.join('-') || code;
}

/**
 * Verify data integrity using hash
 */
export function verifyHash(data: string, expectedHash: string): boolean {
  const actualHash = hashKeccak256(data);
  return actualHash.toLowerCase() === expectedHash.toLowerCase();
}

/**
 * Generate a secure random string (for nonces, etc.)
 */
export function generateRandomString(length: number = 32): string {
  return bytesToHex(randomBytes(length));
}

/**
 * Encrypt AES key with RSA public key
 * Uses Web Crypto API for RSA-OAEP encryption
 * @param aesKey AES-256 key (32 bytes)
 * @param publicKeyPEM RSA public key in PEM format
 * @returns Base64 encoded encrypted key
 */
export async function encryptAESKeyWithRSA(
  aesKey: Uint8Array,
  publicKeyPEM: string
): Promise<string> {
  // Remove PEM header/footer and decode base64
  const pemContents = publicKeyPEM
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '');

  const binaryDer = atob(pemContents);
  const publicKeyBytes = Uint8Array.from(binaryDer, c => c.charCodeAt(0));

  // Import RSA public key
  const publicKey = await crypto.subtle.importKey(
    'spki',
    publicKeyBytes,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );

  // Encrypt AES key with RSA
  const encryptedKey: ArrayBuffer = await crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    publicKey,
    aesKey as BufferSource
  );

  // Convert to base64
  const encryptedKeyBytes = new Uint8Array(encryptedKey);
  return btoa(String.fromCharCode(...encryptedKeyBytes));
}

/**
 * Encrypt both AES key and IV with RSA public key
 * @param aesKey AES-256 key (32 bytes)
 * @param iv AES IV (12 bytes)
 * @param publicKeyPEM RSA public key in PEM format
 * @returns Object with encrypted key and IV
 */
export async function encryptAESKeyAndIV(
  aesKey: Uint8Array,
  iv: Uint8Array,
  publicKeyPEM: string
): Promise<{ encryptedKey: string; encryptedIV: string }> {
  const encryptedKey = await encryptAESKeyWithRSA(aesKey, publicKeyPEM);
  const encryptedIV = await encryptAESKeyWithRSA(iv, publicKeyPEM);

  return {
    encryptedKey,
    encryptedIV,
  };
}
